import { QueryClient } from '@tanstack/react-query';
import { showToast } from '@tix-ticketing/ui-components';
import type { AxiosError } from 'axios';
import type { TokensResponse } from 'api/generated';
import { postApiAuthRefresh } from 'api/generated';
import { client } from 'api/generated/client.gen';
import { isUnadjudicated, markUnadjudicated } from 'lib/auth/session-error';
import { clearSessionStorage, readTokens, writeTokens } from 'lib/auth/token-store';

// Tenant/skin headers are resolved by the backend from the incoming Origin
// and delivered via /api/appsettings-proxy response headers. They're set
// here on init and preserved across updateRequestHeaders() calls.
// eslint-disable-next-line @typescript-eslint/naming-convention
let dynamicHeaders: {
  TenantId?: string;
  SkinUrl?: string;
} = {};
export type ApiClientHeaders = {
  tenantId: string | null;
  skinUrl: string | null;
  protocol: string | null;
  regionalHost: string | null;
};
export const initApiClient = (headers: ApiClientHeaders) => {
  if (headers.protocol && headers.regionalHost) {
    client.setConfig({
      baseURL: `${headers.protocol}://${headers.regionalHost}`
    });
  }
  dynamicHeaders = {
    TenantId: headers.tenantId ?? undefined,
    SkinUrl: headers.skinUrl ?? undefined
  };
};

// The central external-auth redirect flow needs the tenant, skin and API base
// as query params on a full-page navigation to /api/auth/external/start, so
// expose the values the axios client already resolved on init.
export const getApiClientHeaders = () => ({
  tenantId: dynamicHeaders.TenantId ?? null,
  skinUrl: dynamicHeaders.SkinUrl ?? null,
  baseUrl: client.getConfig().baseURL ?? null
});

// The access token is read from the token store rather than passed in, so a
// refresh performed by the interceptor can never be overwritten by a caller
// holding an older copy.
export const updateRequestHeaders = (language: string) => {
  const token = readTokens()?.accessToken;
  client.setConfig({
    headers: {
      ...dynamicHeaders,
      AcceptLanguage: language,
      Culture: language,
      Authorization: token ? `Bearer ${token}` : ''
    }
  });
};
const setAuthorizationHeader = (token: string) => {
  client.setConfig({
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

/**
 * Point the client at whatever the token store currently holds.
 *
 * Needed by the cross-tab session watcher: it has to correct the header
 * *synchronously*, before any refetch it provokes goes out, and React's effect
 * in `AppProviders` runs too late for that.
 */
export const syncAuthorizationHeader = () => {
  setAuthorizationHeader(readTokens()?.accessToken ?? '');
};

/** End the session once, however many requests discovered it was dead. */
const endSession = () => {
  clearSessionStorage();
  setAuthorizationHeader('');
  // Remove, don't invalidate: invalidation refetches the active queries while
  // leaving their data on screen, so the page keeps looking signed in.
  queryClient.clear();
};
const statusOf = (error: unknown) => (error as AxiosError | undefined)?.response?.status;
const bearerToken = (header: unknown) => typeof header === 'string' && header.startsWith('Bearer ') ? header.slice('Bearer '.length) : '';

/** The server rejected the credentials, as opposed to the call not completing
 *  — a 5xx or a dropped connection says nothing about whether the session is
 *  still good, so it must not sign the customer out. */
const isCredentialRejection = (status: unknown) => status === 400 || status === 401 || status === 403;
type RefreshOutcome = {
  tokens: TokensResponse | null;
  rejected: boolean;
};

/**
 * Reads that tolerate an anonymous caller answer 200 under an expired token
 * rather than 401, so nothing replays them and their cached copies go on
 * describing a signed-out buyer. The refresh path calls this from inside the
 * single flight, so a burst of 401s costs one round. `cancelRefetch: false`
 * leaves the replays the renewal is releasing alone, which also makes a repeat
 * call while the first round is still running close to free. Nothing awaits it:
 * a caller that just got its session back must not wait on a refetch.
 */
const refetchReadsTakenWhileExpired = () => {
  void queryClient.invalidateQueries(undefined, {
    cancelRefetch: false
  });
};
const refreshLockName = 'tixly-token-refresh';

// The refresh token is single-use, so the refresh has to be serialised across
// every tab on the origin, not just within this one. Web Locks is unavailable
// outside a secure context; there the module-level latch below still covers
// this tab, which is the common case.
const withRefreshLock = <T,>(run: () => Promise<T>): Promise<T> => typeof navigator !== 'undefined' && 'locks' in navigator ?
// The DOM typing infers the lock's result type from the callback's return
// type rather than awaiting it, so the promise needs flattening by hand.
navigator.locks.request(refreshLockName, run) as unknown as Promise<T> : run();

// One refresh at a time. Every authenticated page fires several requests at
// once, so an expired access token produces a burst of simultaneous 401s; left
// alone each would spend the same refresh token, and with rotation enabled all
// but one would fail and tear down the session the winner had just renewed.
let refreshPromise: Promise<RefreshOutcome> | null = null;
const refreshTokens = (attemptedAccessToken: string): Promise<RefreshOutcome> => {
  refreshPromise ??= withRefreshLock(async (): Promise<RefreshOutcome> => {
    const stored = readTokens();
    if (!stored?.accessToken || !stored?.refreshToken) {
      return {
        tokens: null,
        rejected: true
      };
    }

    // Another tab may have refreshed while this one waited for the lock. Its
    // pair is already in the store and the token this tab meant to spend has
    // been rotated away, so adopt the new one rather than presenting a used
    // refresh token and having the backend reject the whole session.
    if (stored.accessToken !== attemptedAccessToken) {
      setAuthorizationHeader(stored.accessToken);
      refetchReadsTakenWhileExpired();
      return {
        tokens: stored,
        rejected: false
      };
    }
    try {
      const response: any = await postApiAuthRefresh({
        body: {
          accessToken: stored.accessToken,
          refreshToken: stored.refreshToken
        }
      });
      if (response.status !== 200 || !response.data?.accessToken) {
        return {
          tokens: null,
          rejected: isCredentialRejection(response.status)
        };
      }
      const next = response.data as TokensResponse;

      // The store is the authority on which session is live. If it moved on
      // while the refresh was in flight — a sign-out, or a different customer
      // signing in — this pair belongs to a session that no longer exists, and
      // writing it back would resurrect it. The rotated refresh token is
      // forfeit either way: the session it renews is already over.
      const current = readTokens();
      if (current?.accessToken !== stored.accessToken) {
        setAuthorizationHeader(current?.accessToken ?? '');
        return {
          tokens: null,
          rejected: false
        };
      }
      writeTokens(next);
      setAuthorizationHeader(next.accessToken ?? '');
      refetchReadsTakenWhileExpired();
      return {
        tokens: next,
        rejected: false
      };
    } catch (error: any) {
      return {
        tokens: null,
        rejected: isCredentialRejection(error?.response?.status)
      };
    }
  }).catch((): RefreshOutcome => ({
    tokens: null,
    rejected: false
  })).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
};
client.instance.interceptors.response.use(response => response, async error => {
  const originalRequest = error.config;

  // The refresh call failing must never trigger another refresh. Whether the
  // session is over is decided below, where the token that was attempted is
  // known.
  if (originalRequest?.url?.includes('/auth/refresh')) {
    return Promise.reject(error);
  }
  if (error.response?.status !== 401 || originalRequest?._retry) {
    return Promise.reject(error);
  }
  const tokens = readTokens();

  // Nothing to refresh with and nothing to end — a guest hitting a protected
  // endpoint. Stay quiet rather than tearing down a session that was never
  // established.
  if (!tokens?.accessToken) {
    return Promise.reject(error);
  }

  // An access token with no refresh token can never be renewed, so this 401
  // is final. Left in storage it reproduces exactly this failure on every
  // reload, with no path out of it.
  if (!tokens.refreshToken) {
    endSession();
    return Promise.reject(error);
  }
  originalRequest._retry = true;

  // A 401 for a token the store has already moved past is stale news: this
  // request was in flight while another tab, or an earlier refresh here,
  // renewed the session. Replay it rather than spending a refresh token.
  const sentWith = bearerToken(originalRequest.headers?.Authorization ?? originalRequest.headers?.authorization);
  if (sentWith && sentWith !== tokens.accessToken) {
    originalRequest.headers['Authorization'] = `Bearer ${tokens.accessToken}`;
    refetchReadsTakenWhileExpired();
    return client.instance.request(originalRequest);
  }
  const {
    tokens: refreshed,
    rejected
  } = await refreshTokens(tokens.accessToken);
  if (refreshed?.accessToken) {
    originalRequest.headers['Authorization'] = `Bearer ${refreshed.accessToken}`;
    return client.instance.request(originalRequest);
  }

  // Only tear the session down for a genuine rejection, and only while the
  // store still holds the token that was rejected — anything else means a
  // concurrent refresh has already renewed the session.
  if (rejected && readTokens()?.accessToken === tokens.accessToken) {
    endSession();
    return Promise.reject(error);
  }

  // The refresh never reached a verdict: a 5xx or a dropped connection on the
  // way to /auth/refresh, or a session that was replaced while it was in
  // flight. The tokens are untouched and may well still be good, so this 401
  // must not be read as "signed out".
  return Promise.reject(markUnadjudicated(error));
});
export const staletime = {
  SHORT: 60 * 1000,
  MEDIUM: 5 * 60 * 1000,
  LONG: 15 * 60 * 1000
};
export const queryClient = new QueryClient();
queryClient.setDefaultOptions({
  queries: {
    refetchOnWindowFocus: false,
    staleTime: staletime.SHORT
  },
  mutations: {
    onError: error => {
      // A dead or unconfirmable session already has its own UI. A generic
      // toast on top of it just reads as a second, unrelated failure.
      if (isUnadjudicated(error) || statusOf(error) === 401) return;
      showToast({
        title: 'Unexpected Error',
        message: 'An unexpected error occurred',
        severity: 'error'
      });
    }
  }
});